import { translate } from '@web-platform/shared-universal-translations';

import type { BundledConsentVariant } from '@skyscanner-internal/global-components/login-form';
import type { LoginModalReferrer } from '@web-platform/shared-universal-types/src/redux/state/appInitial/user/login';

export const LOGIN_MODAL_REFERRERS: Record<
  NonNullable<LoginModalReferrer>,
  NonNullable<LoginModalReferrer>
> = {
  SAVE_TO_LIST: 'SAVE_TO_LIST',
  PRICE_ALERT: 'PRICE_ALERT',
  HEADER_LOG_IN: 'HEADER_LOG_IN',
  HEADER_SAVE_TO_LIST: 'HEADER_SAVE_TO_LIST',
  PRICING_OPTION_LOYALTY_CTA: 'PRICING_OPTION_LOYALTY_CTA',
  AUTO_PROMPT: 'AUTO_PROMPT',
  FLIGHT_ENTRY_LOYALTY_CTA: 'FLIGHT_ENTRY_LOYALTY_CTA',
  LOYALTY_POC: 'LOYALTY_POC',
  LOYALTY_POC_DAY_VIEW: 'LOYALTY_POC_DAY_VIEW',
  DROPS_CTA: 'DROPS_CTA',
};

const getTitleString = (
  referrer: LoginModalReferrer,
  bundledConsentVariant?: BundledConsentVariant,
): string => {
  switch (referrer) {
    case LOGIN_MODAL_REFERRERS.SAVE_TO_LIST:
      return translate('GCLogin_B1_B4_Headline');
    case LOGIN_MODAL_REFERRERS.PRICE_ALERT:
      return translate('GCLogin_B2_B3_Alerts_Headline');
    case LOGIN_MODAL_REFERRERS.HEADER_LOG_IN:
      return translate('GCLogin_H1_F1_Nav_Headline');
    case LOGIN_MODAL_REFERRERS.PRICING_OPTION_LOYALTY_CTA:
    case LOGIN_MODAL_REFERRERS.FLIGHT_ENTRY_LOYALTY_CTA:
    case LOGIN_MODAL_REFERRERS.LOYALTY_POC:
    case LOGIN_MODAL_REFERRERS.LOYALTY_POC_DAY_VIEW:
      return translate('loyalty_discounts_login_form_heading');
    case LOGIN_MODAL_REFERRERS.AUTO_PROMPT:
      switch (bundledConsentVariant) {
        case 'B':
          return translate('V1_Bundled_GCLogin_Default_All_Headline');
        case 'C':
          return translate('V2_Bundled_GCLogin_Default_All_Headline');
        default:
          return translate('GCLogin_Auto1_Auto2_Headline');
      }
    case LOGIN_MODAL_REFERRERS.DROPS_CTA:
      return translate('GCLogin_Auto1_Auto2_Headline');
    default:
      return translate('GCLogin_Default_All_Headline');
  }
};

const getSubHeadingString = (
  referrer: LoginModalReferrer,
  bundledConsentVariant?: BundledConsentVariant,
): string => {
  switch (referrer) {
    case LOGIN_MODAL_REFERRERS.SAVE_TO_LIST:
      return translate('GCLogin_B1_B2_Subcopy');
    case LOGIN_MODAL_REFERRERS.PRICE_ALERT:
      return translate('GCLogin_B2_B3_Alerts_Subcopy');
    case LOGIN_MODAL_REFERRERS.HEADER_LOG_IN:
      return translate('GCLogin_H1_F1_Nav_Subcopy');
    case LOGIN_MODAL_REFERRERS.PRICING_OPTION_LOYALTY_CTA:
    case LOGIN_MODAL_REFERRERS.FLIGHT_ENTRY_LOYALTY_CTA:
    case LOGIN_MODAL_REFERRERS.LOYALTY_POC:
    case LOGIN_MODAL_REFERRERS.LOYALTY_POC_DAY_VIEW:
      return translate('loyalty_discounts_login_form_text');
    case LOGIN_MODAL_REFERRERS.AUTO_PROMPT:
      switch (bundledConsentVariant) {
        case 'B':
          return translate('V1_Bundled_GCLogin_Default_All_Subcopy');
        case 'C':
          return translate('V2_Bundled_GCLogin_Default_All_Subcopy');
        default:
          return translate('GCLogin_Auto1_Auto2_Subcopy');
      }
    case LOGIN_MODAL_REFERRERS.DROPS_CTA:
      return translate('GCLogin_Auto1_Auto2_Subcopy');
    default:
      return translate('GCLogin_Default_All_Subcopy');
  }
};

const getLegalTextString = (
  referrer: LoginModalReferrer,
  bundledConsentVariant?: BundledConsentVariant,
): string => {
  if (
    referrer === LOGIN_MODAL_REFERRERS.AUTO_PROMPT &&
    (bundledConsentVariant === 'B' || bundledConsentVariant === 'C')
  ) {
    return translate('Bundled_GCLogin_Default_All_LegalText');
  }
  return translate('GCLogin_All_LegalText');
};

const getSuccessString = (
  referrer: LoginModalReferrer,
  bundledConsentVariant?: BundledConsentVariant,
): string => {
  if (
    referrer === LOGIN_MODAL_REFERRERS.AUTO_PROMPT &&
    (bundledConsentVariant === 'B' || bundledConsentVariant === 'C')
  ) {
    return translate('Bundled_GCLogin_Default_All_Success');
  }

  return translate('GCLogin_Success_Headline');
};

export const getLoginStrings = (
  referrer: LoginModalReferrer,
  bundledConsentVariant?: BundledConsentVariant,
) => ({
  accountDetectionError: translate('LOGIN_GENERIC_ERROR_MESSAGE'),
  ariaLabelBack: translate('LOGIN_FORM_BACK_BUTTON'),
  ariaLabelClose: translate('CLOSE_MODAL'),
  introTitle: getTitleString(referrer, bundledConsentVariant),
  otpAwaitLabel: translate('OTP_LOGIN_RESEND_CODE_AWAIT_MESSAGE'),
  otpErrorSendFailure: translate('OTP_LOGIN_RESEND_CODE_ERROR_MESSAGE'),
  otpResendLinkLabel: translate('OTP_LOGIN_RESEND_CODE_LINK'),
  accountCreationDisclaimer: translate('LOGIN_EMAIL_DISCLAIMER_TEXT'),
  defaultTitle: getTitleString(referrer, bundledConsentVariant),
  defaultSubtitle: getSubHeadingString(referrer, bundledConsentVariant),
  introDivider: translate('GCLogin_All_otherlogins'),
  introA11yAppleLoginLabel: translate('a11y_GCLogin_All_AppleCTA'),
  introA11yFacebookLoginLabel: translate('a11y_GCLogin_All_FacebookCTA'),
  introA11yGoogleLoginLabel: translate('a11y_GCLogin_All_GoogleCTA'),
  introA11yPersistentLoginLabel: translate('a11y_GCLogin_All_RememberMe_Checkbox'),
  emailLabel: translate('GCLogin_All_emailCTA_title'),
  emailPlaceholder: translate('GCLogin_All_emailCTA_default_text'),
  emailSubmitButton: translate('GCLogin_All_emailCTA_button'),
  emailError: translate('GCLogin_All_InvalidEmailFormat'),
  rememberMeText: translate('GCLogin_All_RememberMe_Text'),
  legalText: getLegalTextString(referrer, bundledConsentVariant),
  verificationTitle: translate('GCLogin_EmailVerification_All_Headline'),
  verificationSubtitle: translate('GCLogin_EmailVerification_All_Subcopy'),
  verificationSentNotice: translate('GCLogin_EmailVerification_All_SentNotification'),
  verificationCodeLabel: translate('GCLogin_EmailVerification_PINentry_Title'),
  verificationCodeMissing: translate('GCLogin_EmailVerification_PINentry_missingcode'),
  verificationSubmitButton: translate('GCLogin_EmailVerification_PINentry_CTA'),
  verificationCodeError: translate('GCLogin_EmailVerification_InvalidPIN'),
  optInTitle: translate('GCLogin_OptIn_All_Headline'),
  optInSubtitle: translate('GCLogin_OptIn_All_Subcopy'),
  optInAcceptButton: translate('GCLogin_All_OptIn_CTA_Accept'),
  optInDeclineButton: translate('GCLogin_All_OptIn_CTA_Decline'),
  optInLegalText: translate('GCLogin_OptIn_LegalText'),
  successTitle: getSuccessString(referrer, bundledConsentVariant),
  chinaKoreaConsentTitle: translate('GCLogin_Korea_China_Consent_Headline'),
  chinaKoreaConsentButton: translate('GCLogin_Korea_China_Consent_CTA'),
  chinaKoreaConsentError: translate('GCLogin_Korea_China_Consent_Error'),
  koreaPrivacyConsent: translate('GCLogin_Korea_Consent_Checkbox1'),
  koreaTermsConsent: translate('GCLogin_Korea_Consent_Checkbox2'),
  chinaSensitiveDataConsent: translate('GCLogin_China_Consent_Checkbox1'),
  chinaShareDataConsent: translate('GCLogin_China_Consent_Checkbox2'),
  chinaTermsConsent: translate('GCLogin_China_Consent_Checkbox3'),
  regulatoryConsentSubtitle: translate('GCLogin_Korea_China_Consent_Subtitle'),
  a11yPrivacyLink: translate('a11y_GCLogin_All_LegalPrivacyLink_Text'),
  a11yTermsLink: translate('a11y_GCLogin_All_LegalTermsLink_Text'),
  a11yModalDescription: translate('a11y_GCLogin_All_Modal_Text'),
  introModalAriaLabel: translate('a11y_GCLogin_All_Modal_Text'),
  introA11yTermsOfServiceLabel: translate('a11y_GCLogin_All_LegalTermsLink_Text'),
  introA11yPrivacyPolicyLabel: translate('a11y_GCLogin_All_LegalPrivacyLink_Text'),
  regulatoryConsentModalAriaLabel: translate('GCLogin_Korea_China_Consent_Headline'),
  regulatoryConsentModalAriaLabelBack: translate('LOGIN_FORM_BACK_BUTTON'),
});
